import React, { useState, useEffect, useRef } from 'react'
import { useAtomValue, useSetAtom, useAtom } from 'jotai'
import * as state from '../states'
import { Access } from '../lib'
import { IconMail, IconMaximize, IconMinimize, IconLogout, IconSunLine, IconSunFill } from './ui'
import { setStorageToken } from '../hooks'
import { useNavigate, Link } from 'react-router'
import { MENU } from '../constants'
import { ERoleAccess, ELanguage, LANGUAGE_LABEL_MAP } from '../types'
import { useTranslation } from 'react-i18next'
import { setDefaultLocale } from 'react-datepicker'
import { ALL_LANG_KEY } from '../types/lang'
//import { ChristmasLights } from '../components/happyNewYear/ChristmasLights'

const REF_POINT = 150

export const Header = () => {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const setJotaiTokens = useSetAtom(state.tokensAtom)

	const [isMenuOpen, setIsMenuOpen] = useAtom(state.menuIsOpenAtom)
	const [isThemeDark, setIsThemeDark] = useAtom(state.themeIsDarkAtom)

	const [profile, setProfile] = useAtom(state.profileAtom)
	const [showProfile, setShowProfile] = useState<boolean>(false)
	const [showLanguage, setShowLanguage] = useState<boolean>(false)
	const refProfile = useRef<HTMLDivElement>(null)
	const refLanguage = useRef<HTMLDivElement>(null)
	const $access = new Access(profile ?? null)
	const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
	const [isStickyHeader, setIsStickyHeader] = useState<boolean>(false)

	useEffect(() => {
		// Header Sticky logic
		const onScrollHandler = () => {
			window.scrollY >= REF_POINT ? setIsStickyHeader(true) : setIsStickyHeader(false)
		}

		window.addEventListener('scroll', onScrollHandler)
		return () => {
			window.removeEventListener('scroll', onScrollHandler)
		}
	}, [])

	useEffect(() => {
		// Fullscreen logic
		function onFullscreenChange() {
			setIsFullscreen(Boolean(document.fullscreenElement))
		}

		document.addEventListener('fullscreenchange', onFullscreenChange)

		return () => document.removeEventListener('fullscreenchange', onFullscreenChange)
	}, [])

	useEffect(() => {
		// onOutsideClick logic
		if (showProfile || showLanguage) {
			const onOutsideClick = (e: MouseEvent) => {
				const containsProfile = refProfile.current && refProfile.current.contains(e.target as HTMLDivElement)
				const containsLanguage = refLanguage.current && refLanguage.current.contains(e.target as HTMLDivElement)
				//console.log('onOutsideClick: ', {showProfile, containsProfile})
				if (showProfile && !containsProfile) {
					setShowProfile(false)
					//console.log('closing showProfile...!')
				}
				if (showLanguage && !containsLanguage) {
					setShowLanguage(false)
					//console.log('closing showLanguage...!')
				}
			}

			document.addEventListener('click', onOutsideClick)

			return () => {
				document.removeEventListener('click', onOutsideClick)
			}
		}
	}, [showProfile, setShowProfile, showLanguage, setShowLanguage])

	const renderFullscreenBtn = () => {
		const onFullscreenButtonClick = async (event: React.MouseEvent) => {
			if (!isFullscreen) {
				await document.documentElement.requestFullscreen()
			} else {
				await document.exitFullscreen()
			}

			setIsFullscreen(prevValue => !prevValue)
		}

		return (
			<li className="ms-lg-4 ms-md-4 ms-2">
				<div className="dropdown mail">
					<button
						className={`btn btn-secondary fullscreen-btn border-0 p-0 position-relative ${isFullscreen ? 'active' : ''}`}
						id="fullscreen-button"
						onClick={onFullscreenButtonClick}
					>
						<span className="maximize">
							<IconMaximize/>
						</span>
						<span className="minimize top-50 start-50 translate-middle position-absolute">
							<IconMinimize/>
						</span>
					</button>
				</div>
			</li>
		)
	}

	const renderBurgerMenuBtn = () => {
		const onBurgerMenuBtnClick = (event: React.MouseEvent) => {
			setIsMenuOpen(state.toggleMenu)
		}

		return (
			<li>
				<div className={`burger-menu d-none d-lg-block ${isMenuOpen ? 'active' : ''}`}
				     onClick={onBurgerMenuBtnClick}
				>
					<span className="top-bar"></span>
					<span className="middle-bar"></span>
					<span className="bottom-bar"></span>
				</div>
				<div className={`responsive-burger-menu d-block d-lg-none ${isMenuOpen ? 'active' : ''}`}
				     onClick={onBurgerMenuBtnClick}
				>
					<span className="top-bar"></span>
					<span className="middle-bar"></span>
					<span className="bottom-bar"></span>
				</div>
			</li>
		)
	}

	const renderLanguageBtn = () => {
		const changeLanguage = (lang: ELanguage) => (event: React.MouseEvent) => {
			event.preventDefault()
			i18n.changeLanguage(lang)
			setDefaultLocale(lang) // set a global default locale for react-datepicker
			setShowLanguage(false)
		}

		//console.log('!renderLanguageBtn:: ', { currentLanguage: i18n.language })

		const _getFlagIconName = (lang: ELanguage) => {
			// i18n.language
			switch (lang) {
				case ELanguage.ua: {
					return 'flag-icon-ua'
				}
				case ELanguage.ru: {
					return 'flag-icon-ru'
				}
				case ELanguage.en:
				default: {
					return 'flag-icon-gb'
				}
			}
		}

		return (
			<li>
				<div className="dropdown mail languages"
				     ref={refLanguage}
				>
					<button className="btn btn-secondary border-0 p-0 show"
					        type="button"
					        onClick={() => setShowLanguage(!showLanguage)}
					>
						{/*<img src="/assets/images/flags/us.svg" alt="usa"/>*/}
						<span className={`flag-icon ${_getFlagIconName(i18n.language as ELanguage)}`}></span>
					</button>

					<div className={`dropdown-menu p-0 border-0 rounded box-shadow ${showLanguage ? 'show' : ''}`}
					>
						<h6
							className="dropdown-item-text fs-15 fw-semibold m-0 py-3 border-bottom border-color d-flex justify-content-between align-items-center">
							{`${t(ALL_LANG_KEY.general.header_language_label)}`}
							<span className="text-white bg-danger fs-12 py-1 px-1 rounded-1 fw-normal">0{Object.entries(ELanguage).length}</span>
						</h6>

						<div className="d-flex flex-column">
							{Object.entries(ELanguage).map(([key, val], i, arr) => {
								const isLastItem = i === arr.length - 1
								const borderClassName = !isLastItem ? 'border-bottom border-color' : ''
								const activeClassName = i18n.language.includes(val) ? 'active' : ''
								const flagIconName = _getFlagIconName(val)
								const flagLabel = LANGUAGE_LABEL_MAP[val]

								return (
									<Link key={`dropdown-language-${val}-${i}`}
									      to="#"
									      className={`dropdown-item py-2 ${borderClassName} ${activeClassName}`}
									      onClick={changeLanguage(val)}
									>
										<div className="d-flex align-items-center">
											<div className="avatar-md rounded-circle text-center flex-shrink-0">
												<span className={`flag-icon ${flagIconName}`}></span>
											</div>
											<div className="flex-grow-1 ms-2 text-truncate">
												<h6 className="my-0 fs-14 fw-medium">{flagLabel}</h6>
											</div>
										</div>
									</Link>
								)
							})}
						</div>
					</div>
				</div>
			</li>
		)
	}

	const renderThemeToggleBtn = () => {
		const onThemeToggleBtnClick = (event: React.MouseEvent) => {
			setIsThemeDark(state.toggleTheme)
		}

		return (
			<li className="ms-lg-4 ms-md-4 ms-2">
				<div className="dropdown mail box-svg-fill">
					<button
						className={`btn btn-secondary fullscreen-btn theme-toggle-btn border-0 p-0 position-relative ${isThemeDark ? 'active' : ''}`}
						id="theme-toggle-btn"
						onClick={onThemeToggleBtnClick}
					>
						<span className="maximize">
							<IconSunLine/>
						</span>
						<span className="minimize top-50 start-50 translate-middle position-absolute">
							<IconSunFill/>
						</span>
					</button>
				</div>
			</li>
		)
	}

	const renderDropdownMail = () => {
		// TODO: check the link to tech support ? (now is to @cto_invisible)
		return (
			<li className="ms-lg-4 ms-md-4 ms-2">
				<div className="dropdown mail">
					<Link className="btn btn-secondary border-0 p-0"
					      type="button"
					      target="_blank"
					      to="https://t.me/cto_invisible"
					>
						<IconMail/>
					</Link>
				</div>
			</li>
		)
	}

	const renderDropdownUserProfile = () => {
		const profileEmail = profile?.email ?? ''
		const profileLoginRole = $access.$role() && $access.$account() ? `${$access.$account()?.auth?.login} (${$access.$role()?.name})` : ''

		const onLogout = (event: React.MouseEvent): void => {
			//TODO: check the logic --- to reset ALL data from Jotai (Jotai) state too (commonOptions...)???
			event.preventDefault()
			setProfile(null)
			setJotaiTokens({})
			setStorageToken(null)
			navigate(MENU.logout.url)
		}

		const _renderProfileInfo = () => {
			return <>
				<h3 className="fs-14 mb-0">{profileEmail}</h3>
				<span
					className="fs-13 text-body">{profileLoginRole}</span>
			</>
		}

		return (
			<li className="ms-lg-4 ms-md-4 ms-2">
				<div className="dropdown user-profile"
				     ref={refProfile}
				>
					<div className="btn border-0 p-0 d-flex align-items-center text-start"
					     onClick={() => setShowProfile(!showProfile)}
					>
						<div className="flex-shrink-0">
							<img className="rounded-circle user"
							     src="/assets/images/user-avatar-default.png" alt="user"
							/>
						</div>
						<div className="flex-grow-1 ms-2 d-none d-xxl-block">
							{_renderProfileInfo()}
						</div>
					</div>

					<ul className={`dropdown-menu border-0 rounded box-shadow ${showProfile ? 'show' : ''}`}>
						<li>
							<div className="text-center border-bottom border-color pb-10 mb-10 d-xxl-none">
								{_renderProfileInfo()}
							</div>
						</li>
						{/*<li>
							<div className="dropdown-item d-flex align-items-center text-body">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-dollar-sign">
									<line x1="12" y1="1" x2="12" y2="23"></line>
									<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
								</svg>
								<span className="ms-2 fs-14 fw-semibold text-dark">45,8745.48</span>
							</div>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="profile.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-user">
									<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
									<circle cx="12" cy="7" r="4"></circle>
								</svg>
								<span className="ms-2 fs-14">Profile</span>
							</a>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="user-profile.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-command">
									<path
										d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path>
								</svg>
								<span className="ms-2 fs-14">User Profile</span>
							</a>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="team.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-users">
									<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
									<circle cx="9" cy="7" r="4"></circle>
									<path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
									<path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
								</svg>
								<span className="ms-2 fs-14">Team</span>
							</a>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="user.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-list">
									<line x1="8" y1="6" x2="21" y2="6"></line>
									<line x1="8" y1="12" x2="21" y2="12"></line>
									<line x1="8" y1="18" x2="21" y2="18"></line>
									<line x1="3" y1="6" x2="3.01" y2="6"></line>
									<line x1="3" y1="12" x2="3.01" y2="12"></line>
									<line x1="3" y1="18" x2="3.01" y2="18"></line>
								</svg>
								<span className="ms-2 fs-14">User List</span>
							</a>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="add-user.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-user-plus">
									<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
									<circle cx="8.5" cy="7" r="4"></circle>
									<line x1="20" y1="8" x2="20" y2="14"></line>
									<line x1="23" y1="11" x2="17" y2="11"></line>
								</svg>
								<span className="ms-2 fs-14">Add User</span>
							</a>
						</li>*/}
						{/*<li>
							<a className="dropdown-item d-flex align-items-center text-body" href="account-settings.html">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
								     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								     className="feather feather-settings">
									<circle cx="12" cy="12" r="3"></circle>
									<path
										d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
								</svg>
								<span className="ms-2 fs-14">Settings</span>
							</a>
						</li>*/}

						<li>
							<Link className="dropdown-item d-flex align-items-center text-body"
							      to={MENU.logout.url}
							      onClick={onLogout}
							>
								<IconLogout/>
								<span className="ms-2 fs-14">{`${t(MENU.logout.title)}`}</span>
							</Link>
						</li>
					</ul>
				</div>
			</li>
		)
	}

	return (
		<header className={`header-area bg-white mb-24 ${isStickyHeader ? 'is-sticky' : ''}`}>
			<div className={'position-relative'}>
				{/*activate HappyNewYear feature when necessary*/}
				{/*<ChristmasLights/>*/}
				<div className="row align-items-center">
					<div className="col-lg-6 col-sm-6">
						<div className="header-left-content">
							<ul
								className="list-unstyled ps-0 mb-0 d-flex justify-content-center justify-content-sm-start align-items-center"
							>
								{renderBurgerMenuBtn()}

								{/*<li>
								<form className="src-form position-relative z-1">
									<input type="text" className="form-control" placeholder="Search Here"/>
									<button
										className="bg-transparent position-absolute position-absolute top-50 end-0 translate-middle border-0 ps-0 pe-1">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
										     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
										     className="feather feather-search">
											<circle cx="11" cy="11" r="8"></circle>
											<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
										</svg>
									</button>
								</form>
							</li>*/}
							</ul>
						</div>
					</div>
					<div className="col-lg-6 col-sm-6">
						<div className="header-right-content float-lg-end float-md-end">
							<ul
								className="list-unstyled ps-0 mb-0 d-flex justify-content-center justify-content-sm-end align-items-center">

								{/*<li className="ms-lg-4 ms-md-4 ms-2">
								<div className="dropdown notifications">
									<button className="btn btn-secondary border-0 p-0 position-relative badge" type="button"
									        data-bs-toggle="dropdown" aria-expanded="false">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
										     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
										     className="feather feather-bell">
											<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
											<path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
										</svg>
									</button>
									<div className="dropdown-menu dropdown-lg p-0 border-0 box-shadow" style={{}}>
										<h6
											className="dropdown-item-text fs-15 fw-semibold m-0 py-3 border-bottom border-color d-flex justify-content-between align-items-center">
											Notifications
											<span className="text-white bg-danger fs-12 py-1 px-1 rounded-1 fw-normal">08</span>
										</h6>

										<div className="notification-menu h-400" data-simplebar="init">
											<div className="simplebar-wrapper" style={{ margin: '0' }}>
												<div className="simplebar-height-auto-observer-wrapper">
													<div className="simplebar-height-auto-observer"></div>
												</div>
												<div className="simplebar-mask">
													<div className="simplebar-offset" style={{ right: '0', bottom: '0' }}>
														<div className="simplebar-content-wrapper" style={{ height: 'auto', overflow: 'hidden' }}>
															<div className="simplebar-content" style={{ padding: 0 }}>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">6 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-check-circle">
																				<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
																				<polyline points="22 4 12 14.01 9 11.01"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Order Placed <span className="text-success">ID: #1116773</span>
																			</h6>
																			<small className="mb-0 text-body fs-12">Order Placed Successfully</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">8 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-clock">
																				<circle cx="12" cy="12" r="10"></circle>
																				<polyline points="12 6 12 12 16 14"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Order Delayed <span className="text-danger">ID: 7731116</span>
																			</h6>
																			<small className="mb-0 text-body fs-12">Order Delayed Unfortunately</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">1 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-shopping-bag">
																				<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
																				<line x1="3" y1="6" x2="21" y2="6"></line>
																				<path d="M16 10a4 4 0 0 1-8 0"></path>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Your Order Has Been Shipped</h6>
																			<small className="mb-0 text-body fs-12">Order No: 123456 Has Shipped To Your
																				Delivery Address</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">3 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-percent">
																				<line x1="19" y1="5" x2="5" y2="19"></line>
																				<circle cx="6.5" cy="6.5" r="2.5"></circle>
																				<circle cx="17.5" cy="17.5" r="2.5"></circle>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Discount Available</h6>
																			<small className="mb-0 text-body fs-12">Discount Available On Selected
																				Products</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">4 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-user-check">
																				<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
																				<circle cx="8.5" cy="7" r="4"></circle>
																				<polyline points="17 11 19 13 23 9"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Account Has Been Verified</h6>
																			<small className="mb-0 text-body fs-12">Your Account Has Been Verified
																				Sucessfully</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">6 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-check-circle">
																				<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
																				<polyline points="22 4 12 14.01 9 11.01"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Order Placed <span className="text-success">ID: #1116773</span>
																			</h6>
																			<small className="mb-0 text-body fs-12">Order Placed Successfully</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">8 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-clock">
																				<circle cx="12" cy="12" r="10"></circle>
																				<polyline points="12 6 12 12 16 14"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Order Delayed <span className="text-danger">ID: 7731116</span>
																			</h6>
																			<small className="mb-0 text-body fs-12">Order Delayed Unfortunately</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">1 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-shopping-bag">
																				<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
																				<line x1="3" y1="6" x2="21" y2="6"></line>
																				<path d="M16 10a4 4 0 0 1-8 0"></path>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Your Order Has Been Shipped</h6>
																			<small className="mb-0 text-body fs-12">Order No: 123456 Has Shipped To Your
																				Delivery Address</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">3 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-percent">
																				<line x1="19" y1="5" x2="5" y2="19"></line>
																				<circle cx="6.5" cy="6.5" r="2.5"></circle>
																				<circle cx="17.5" cy="17.5" r="2.5"></circle>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Discount Available</h6>
																			<small className="mb-0 text-body fs-12">Discount Available On Selected
																				Products</small>
																		</div>
																	</div>
																</a>
																<a href="notifications.html" className="dropdown-item py-3">
																	<small className="float-end ps-2 text-body fs-12">4 min ago</small>
																	<div className="d-flex align-items-center">
																		<div className="avatar-md rounded-circle text-center flex-shrink-0">
																			<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
																			     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
																			     strokeLinejoin="round" className="feather feather-user-check">
																				<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
																				<circle cx="8.5" cy="7" r="4"></circle>
																				<polyline points="17 11 19 13 23 9"></polyline>
																			</svg>
																		</div>
																		<div className="flex-grow-1 ms-2 text-truncate">
																			<h6 className="my-0 fs-14 fw-medium">Account Has Been Verified</h6>
																			<small className="mb-0 text-body fs-12">Your Account Has Been Verified
																				Sucessfully</small>
																		</div>
																	</div>
																</a>
															</div>
														</div>
													</div>
												</div>
												<div className="simplebar-placeholder" style={{ width: '0', height: '0' }}></div>
											</div>
											<div className="simplebar-track simplebar-horizontal" style={{ visibility: 'hidden' }}>
												<div className="simplebar-scrollbar"
												     style={{ transform: 'translate3d(0px, 0px, 0px)', display: 'none' }}></div>
											</div>
											<div className="simplebar-track simplebar-vertical" style={{ visibility: 'hidden' }}>
												<div className="simplebar-scrollbar"
												     style={{
													     transform: 'translate3d(0px, 0px, 0px)',
													     display: 'none',
													     height: '213px'
												     }}></div>
											</div>
										</div>
										<a href="notification.html"
										   className="dropdown-item text-center text-white border-top border-color pt-2 pb-2 d-block bg-primary rounded-bottom fs-15">
											View all
										</a>
									</div>
								</div>
							</li>*/}

								{/*TODO: remove render condition after 'TRANSLATE FEATURE' is done*/}
								{!!$access.$(ERoleAccess.DebugDraft) ? renderLanguageBtn() : null}

								{renderThemeToggleBtn()}

								{renderFullscreenBtn()}

								{renderDropdownMail()}

								{renderDropdownUserProfile()}
							</ul>
						</div>
					</div>
				</div>
			</div>
		</header>
	)
}